<template>
  <component
    :is="tag"
    :class="[
      'btn', 
      `btn-${variant}`, 
      { 
        'btn-disabled': disabled || loading,
        'btn-sm': size === 'sm',
        'btn-lg': size === 'lg',
        'btn-close': variant === 'close',
        'btn-expand': variant === 'expand',
        'btn-call-client': variant === 'call-client',
        'btn-profile': variant === 'profile',
        'btn-mark-all-read': variant === 'mark-all-read',
        'btn-delete-notification': variant === 'delete-notification',
        'btn-editing': editing,
        'btn-loading': loading
      }
    ]"
    :disabled="isLink ? undefined : (disabled || loading)"
    :type="isLink ? undefined : type"
    :rel="relAttr"
    :aria-disabled="isLink && (disabled || loading) ? 'true' : undefined"
    :aria-label="ariaLabel || (icon && !$slots.default ? getIconLabel() : undefined)"
    :aria-describedby="ariaDescribedBy"
    :aria-busy="loading"
    :role="isLink ? undefined : 'button'"
    v-bind="{ ...destination, ...$attrs }"
    @click="onClick"
  >
    <span v-if="loading && !spinIcon" class="btn-spinner" aria-hidden="true"></span>
    <i v-if="icon && (!loading || spinIcon)" :class="[icon, { 'fa-spin': loading && spinIcon }]" class="btn-icon" aria-hidden="true"></i>
    <slot></slot>
  </component>
</template>

<script setup>
/**
 * Bouton du design system — et, quand l'action mène quelque part, lien.
 *
 * UN SEUL GABARIT POUR LES TROIS FORMES : mêmes classes, même icône, même état
 * de chargement ; seule l'enveloppe change, selon que l'action agit sur place
 * (`<button>`) ou mène quelque part (`<a>` externe, `<router-link>` interne).
 * L'explication vit ici, dans le docblock, et non en commentaire de gabarit :
 * c'est une préférence de lecture, PAS une contrainte technique. Un commentaire
 * de premier niveau dans un `<template>` ne casse rien — Vue 3 marque ce cas
 * (`DEV_ROOT_FRAGMENT`) et continue de transmettre `class`, `style` et les
 * attributs à l'unique élément racine ; en production le commentaire est même
 * retiré à la compilation. Ne pas en faire une règle : quatre composants du kit
 * (ListBody, ListGrid, ListFooterBar, FilterField) commencent par un
 * commentaire et transmettent parfaitement leurs attributs.
 *
 * POURQUOI `href` / `to`. Une action de page n'est pas toujours un geste : le
 * formulaire public d'inscription, une documentation, un export direct sont des
 * DESTINATIONS. Rendues en `<button>` + `router.push`, elles perdent tout ce
 * qu'un lien sait faire (ouvrir dans un onglet, se copier, se cliquer à la
 * molette) et l'utilisateur ne voit rien de cette perte. Faute de ces props,
 * la liste des formateurs avait écrit son action en `<a class="btn btn-primary">`
 * — une classe globale héritée, interdite en v2 (C40) — et repayé en CSS local
 * l'apparence que ce composant sait déjà donner. La même impasse attendait
 * chaque liste ayant une action-lien : le contournement se serait recopié.
 *
 * L'AJOUT EST STRICTEMENT ADDITIF. Sans `href` ni `to`, le composant rend le
 * `<button>` exact d'avant, attributs compris (`type`, `disabled`,
 * `role="button"`). Les 300 fichiers qui le montent ne bougent pas.
 *
 * CE QUI DIFFÈRE POUR UN LIEN. `type` et `disabled` n'existent pas sur `<a>` :
 * les poser produirait du HTML invalide que le navigateur ignore, donc un
 * bouton « désactivé » toujours cliquable. Un lien indisponible annonce donc
 * `aria-disabled` aux technologies d'assistance, et son clic est neutralisé —
 * à la fois en CSS (pointer-events) et en JavaScript, parce que le clavier
 * atteint le lien même quand la souris ne le peut plus. Et `target="_blank"`
 * appelle `rel="noopener noreferrer"` : sans lui, la page ouverte garde une
 * poignée sur la nôtre.
 */
import { computed, useAttrs } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary',
      'secondary',
      'success',
      'danger',
      'export',
      'reset',
      // Actions discrètes d'un contenu dense (case de grille, ligne éditable) :
      // pictogramme gris, cadre au survol seulement. Voir buttons.css.
      'ghost',
      'ghost-danger',
      'close',
      'expand',
      'call-client',
      'profile',
      'mark-all-read',
      'delete-notification'
    ].includes(value)
  },
  disabled: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'button'
  },
  icon: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: null,
    validator: (value) => !value || ['sm', 'lg'].includes(value)
  },
  editing: {
    type: Boolean,
    default: false
  },
  spinIcon: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: null
  },
  ariaDescribedBy: {
    type: String,
    default: null
  },
  /** Destination EXTERNE (ou hors du routeur) : rend un `<a href>`. */
  href: {
    type: String,
    default: null
  },
  /** Destination INTERNE : rend un `<router-link>` (chemin ou objet de route). */
  to: {
    type: [String, Object],
    default: null
  }
})

const emit = defineEmits(['click'])

const attrs = useAttrs()

// `href` l'emporte sur `to` : une adresse absolue ne se route pas.
const tag = computed(() => {
  if (props.href) return 'a'
  if (props.to) return 'router-link'
  return 'button'
})

const isLink = computed(() => tag.value !== 'button')

/**
 * La destination se pose en bloc, et UNIQUEMENT sur la forme concernée.
 *
 * Écrire `:href="tag === 'a' ? href : undefined"` transmettait `href: undefined`
 * à `RouterLink`, pour qui `href` n'est pas une prop : il partait donc dans ses
 * `$attrs`, appliqués APRÈS le `href` qu'il calcule, et l'effaçait. Le lien
 * naviguait toujours au clic, mais sans adresse : impossible de l'ouvrir dans
 * un onglet, de le copier ou d'en voir la cible au survol — c'est-à-dire tout
 * ce pour quoi on rend un lien plutôt qu'un bouton.
 */
const destination = computed(() => {
  if (tag.value === 'a') return { href: props.href }
  if (tag.value === 'router-link') return { to: props.to }
  return {}
})

// Un onglet neuf ne doit pas garder de poignée sur la page qui l'a ouvert.
// Un `rel` fourni par l'appelant reste prioritaire : `$attrs` est appliqué après.
const relAttr = computed(() => {
  if (!isLink.value) return undefined
  if (attrs.rel) return undefined
  return attrs.target === '_blank' ? 'noopener noreferrer' : undefined
})

const onClick = (event) => {
  // Un `<button disabled>` ne déclenche jamais de clic ; un lien, si. On lui
  // rend donc le silence que l'attribut natif assure de l'autre côté.
  if (isLink.value && (props.disabled || props.loading)) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('click', event)
}

const getIconLabel = () => {
  const iconLabels = {
    'fas fa-times': 'Fermer',
    'fas fa-edit': 'Modifier',
    'fas fa-trash': 'Supprimer',
    'fas fa-save': 'Enregistrer',
    'fas fa-plus': 'Ajouter',
    'fas fa-search': 'Rechercher',
    'fas fa-download': 'Télécharger',
    'fas fa-upload': 'Envoyer',
    'fas fa-check': 'Valider',
    'fas fa-arrow-left': 'Retour',
    'fas fa-arrow-right': 'Suivant',
    'fas fa-sync-alt': 'Actualiser'
  }
  return iconLabels[props.icon] || 'Bouton'
}
</script>

<style scoped>
/* Les styles de base .btn et toutes les variantes sont maintenant dans styles/buttons.css */
/* On garde uniquement les styles spécifiques qui diffèrent ou qui ne sont pas dans le global */

.btn-icon {
  font-size: 0.9rem;
}

.btn-loading {
  position: relative;
  pointer-events: none;
}

/* Uniquement les liens : un `<button disabled>` est déjà inerte pour le
   navigateur, alors qu'un `<a>` reste cliquable quoi qu'on lui déclare. */
.btn[aria-disabled='true'] {
  pointer-events: none;
}

.btn-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: btn-spin 0.6s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
